import React, { useState } from "react";
import { hcConfirmar, hcError, hcExito } from "../../../../HC/hcSwal";
import SeccionCard from "../SeccionCard";
import {
  createPDFimagen,
  createPDFimagenExterna,
  createPDFInterconsulta,
  createPDFlaboratorio,
  textoErrorPdf,
} from "../../../../../Service/Service.laboratorioPDF";
import { anularOrdenImagenService } from "../../../../../Service/Service.examen";
import { CITA_FINALIZADA_SENTINEL } from "../../../../../utils/citaFinalizadaAlert";
import PickerModal from "../../../../HC/PickerModal";
import { IconoTipoOrden, etiquetaTipoOrden } from "../../../../HC/HCIconos";

/* ============================================================================
   Órdenes de imagen y laboratorio (SEGÚN EL CASO — nunca avisa ni bloquea).

   «+ Nueva orden» abre un PickerModal con todas las categorías del catálogo a
   la vista; elegir una abre su formulario. Se abre SOBRE la consulta: no
   navega, no desmonta nada, no se pierde lo escrito.

   Los estados que se muestran son los reales del registro (`examen.estado`),
   no etiquetas inventadas.
   ========================================================================== */
/* Cada tipo de orden imprime con su propio generador y su propia clave
   primaria — el mismo mapeo que hacía Table/TableExamen.

   NO hay botón de "Cotización" a propósito. Existe el endpoint
   `genera-pdf-laboratorio-cotizacion/:id`, pero su controller es una copia del
   de laboratorio y renderiza EL MISMO template (`laboratorioPDF.ejs`), que no
   lleva precios: produce un documento idéntico al de "Imprimir". La cotización
   no está implementada, así que el botón solo prometía algo que no ocurre. */
function normalizarOrdenes(cita) {
  const out = [];
  (cita?.examen_cita_laboratorio || []).forEach((e) =>
    out.push({
      tipo: "Laboratorio",
      id: `lab-${e.idexamen_cita}`,
      idReal: e.idexamen_cita,
      imprimir: createPDFlaboratorio,
      fecha: e.fecha,
      numero_orden: e.numero_orden,
      estado: e.estado || "Enviada",
      n: (e.examen_cita_paciente_solicitud || []).length,
    })
  );
  (cita?.examen_imagen_orden || []).forEach((e) =>
    out.push({
      tipo: "Imagen",
      id: `img-${e.idexamen_imagen}`,
      idReal: e.idexamen_imagen,
      imprimir: createPDFimagen,
      // Solo las órdenes de imagen tienen anulación real hoy (ORDENES-ANULAR-01).
      anulable: true,
      fecha: e.fecha,
      numero_orden: e.numero_orden,
      estado: e.estado || "Enviada",
      n: (e.examen_imagen_orden_solicitud || []).length,
    })
  );
  (cita?.examen_imagen_externa_orden || []).forEach((e) =>
    out.push({
      tipo: "Imagen externa",
      id: `imgx-${e.idexamen_imagen_externa}`,
      idReal: e.idexamen_imagen_externa,
      imprimir: createPDFimagenExterna,
      fecha: e.fecha,
      numero_orden: e.numero_orden,
      estado: e.estado || "Enviada",
      n: (e.examen_imagen_externa_orden_solicitud || []).length,
    })
  );
  (cita?.examen_interconsulta_orden || []).forEach((e) =>
    out.push({
      tipo: "Interconsulta",
      // la PK de interconsulta se llama `idexamen_imagen` (así está en el modelo)
      id: `ic-${e.idexamen_imagen}`,
      idReal: e.idexamen_imagen,
      imprimir: createPDFInterconsulta,
      fecha: e.fecha,
      numero_orden: e.numero_orden,
      estado: e.estado || "Enviada",
      n: (e.examen_interconsulta_orden_solicitud || []).length,
    })
  );
  // Las anuladas dejan de listarse: la cabecera se conserva en la base (el
  // hecho de haber emitido la orden no se borra), pero no es una orden viva.
  return out.filter((o) => String(o.estado).toLowerCase() !== "anulada");
}

async function imprimir(orden) {
  const fn = orden.imprimir;
  if (!fn || !orden.idReal) return;
  const r = await fn(orden.idReal);
  if (r && r.success === false) {
    hcError("No se pudo generar el PDF", textoErrorPdf(r));
  }
}

export default function SeccionOrdenes({
  cita,
  catalogos = [],
  bloqueado,
  puedeAnular = false, // permiso ordenes.anular (fail-closed: sin permiso, no se muestra)
  puedeImprimir = false, // permiso ordenes.imprimir (fail-closed, igual que anular)
  onAbrirFormulario, // (categoria) => void  — abre el modal existente
  onRefrescar,
  onNavegar,
}) {
  const [panel, setPanel] = useState(false);
  const [anulando, setAnulando] = useState(null);
  const ordenes = normalizarOrdenes(cita);

  const anular = async (orden) => {
    const confirmacion = await hcConfirmar({
      titulo: "¿Anular esta orden?",
      html:
        `<p><b>${orden.tipo} · ${orden.numero_orden || "s/n"}</b> con ${orden.n} estudio(s).</p>` +
        `<p>Se anula la orden y <b>se revierten los cargos por cobrar</b> que generó. ` +
        `Si alguno ya está facturado, no se podrá anular desde aquí.</p>`,
      input: "text",
      inputPlaceholder: "Motivo (opcional)",
      confirmar: "Anular orden",
      cancelar: "No, volver",
      peligroso: true,
    });
    if (!confirmacion.isConfirmed) return;

    setAnulando(orden.id);
    const r = await anularOrdenImagenService(orden.idReal, confirmacion.value || undefined);
    setAnulando(null);

    if (r === CITA_FINALIZADA_SENTINEL) return; // ya se avisó
    if (!r || r.success !== true) {
      // El backend explica el motivo real (ya facturada, sin trazabilidad…).
      hcError("No se anuló la orden", r?.message || "Intente nuevamente.");
      return;
    }
    hcExito(r.message);
    if (onRefrescar) await onRefrescar();
  };

  return (
    <SeccionCard
      seccionId="ordenes"
      headerExtra={<span className="cev__tag cev__tag--grey">{ordenes.length}</span>}
      onNavegar={onNavegar}
    >
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button type="button" className="cev__btn cev__btn--pri cev__btn--sm" disabled={bloqueado} onClick={() => setPanel(true)}>
          + Nueva orden
        </button>
      </div>

      {ordenes.length === 0 ? (
        <div className="cev__empty">Sin órdenes en esta consulta.</div>
      ) : (
        <table className="cev__list">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>N.º orden</th>
              <th>Estudios</th>
              <th>Estado</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {ordenes.map((o) => (
              <tr key={o.id}>
                <td>{o.tipo}</td>
                <td>{o.numero_orden || "—"}</td>
                <td>{o.n}</td>
                <td><span className="cev__tag cev__tag--grey">{o.estado}</span></td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {puedeImprimir && (
                    <button
                      type="button"
                      className="cev__btn cev__btn--sm"
                      title="Imprimir la orden"
                      onClick={() => imprimir(o)}
                    >
                      Imprimir
                    </button>
                  )}
                  {o.anulable && puedeAnular && (
                    <button
                      type="button"
                      className="cev__btn cev__btn--ghost cev__btn--sm"
                      style={{ color: "var(--danger-text)" }}
                      title="Anular la orden y revertir sus cargos por cobrar"
                      disabled={bloqueado || anulando === o.id}
                      onClick={() => anular(o)}
                    >
                      {anulando === o.id ? "Anulando…" : "Anular"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Antes esto era un panel con pestañas Imagen/Laboratorio/Enviadas: dos
          clics para llegar a la categoría, y una pestaña "Enviadas" que repetía
          la tabla que ya está justo aquí arriba. Ahora es un selector plano:
          todas las categorías a la vista, un clic. */}
      <PickerModal
        isOpen={panel}
        title="Nueva orden"
        subtitle="Elige el tipo de orden; después seleccionas los estudios."
        options={(catalogos || []).filter((c) => !c.__cert).map((c) => ({
          key: c.__key,
          titulo: c.Descripcion,
          descripcion: etiquetaTipoOrden(c.Tipo),
          icono: <IconoTipoOrden tipo={c.Tipo} />,
        }))}
        onSelect={(key) => {
          const cat = (catalogos || []).find((c) => c.__key === key);
          setPanel(false);
          if (cat) onAbrirFormulario(cat);
        }}
        onClose={() => setPanel(false)}
      />
    </SeccionCard>
  );
}
